import Tooltip from '@mui/material/Tooltip'
import {Handle, Position, type NodeProps} from '@xyflow/react'
import {useCallback, useRef, useState} from 'react'

import {useFlowContext} from '../FlowContext'

const baseClasses =
  'group relative rounded px-2 py-1 text-base font-medium leading-6 whitespace-nowrap border max-w-[162px]'

const textClasses = 'block overflow-hidden text-ellipsis'

const dotHandleBase: React.CSSProperties = {
  width: 12,
  height: 12,
  background: 'white',
  border: '2px solid var(--primary_faded_20)',
  opacity: 0,
  transition: 'opacity 0.15s ease, background 0.15s ease, border-color 0.15s ease'
}

const dotHandleFilled: React.CSSProperties = {
  ...dotHandleBase,
  background: 'var(--primary)',
  border: '2px solid var(--primary)'
}

const positions = [
  {id: 'top', position: Position.Top},
  {id: 'right', position: Position.Right},
  {id: 'bottom', position: Position.Bottom},
  {id: 'left', position: Position.Left}
] as const

const variantClasses = {
  default:
    'bg-primary_faded_10 border-primary_faded_20 text-primary hover:bg-primary_faded_10_hover hover:border-primary_faded_20_hover',
  selected: 'bg-primary_faded_10_selected border-primary_faded_20_selected text-primary',
  terminal:
    'bg-neutral_faded_10 border-neutral_faded_20 text-foreground_faded hover:bg-neutral_faded_10_hover hover:border-neutral_faded_20_hover'
}

export const FlowNode: React.FC<NodeProps> = ({id, data, selected, dragging}) => {
  const {terminalNodeId} = useFlowContext()
  const isTerminal = terminalNodeId === id
  const textRef = useRef<HTMLSpanElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  const [isHovered, setIsHovered] = useState(false)
  const [hoveredDot, setHoveredDot] = useState<string | null>(null)

  const checkTruncation = useCallback(() => {
    const el = textRef.current
    if (el) setIsTruncated(el.scrollWidth > el.clientWidth)
  }, [])

  const variantKey = isTerminal ? 'terminal' : selected ? 'selected' : 'default'

  const label = data.label as string
  const showTooltip = isTruncated && !dragging

  const getDotStyle = (dotId: string): React.CSSProperties => ({
    ...(hoveredDot === dotId ? dotHandleFilled : dotHandleBase),
    opacity: isHovered ? 1 : 0
  })

  return (
    <Tooltip title={label} placement="bottom" disableHoverListener={!showTooltip} enterDelay={800}>
      <div
        onMouseEnter={() => {
          checkTruncation()
          setIsHovered(true)
        }}
        onMouseLeave={() => setIsHovered(false)}
        className={`${baseClasses} ${variantClasses[variantKey]}`}
      >
        {positions.map(({id: pos, position}) => (
          <Handle
            key={pos}
            type={isTerminal ? 'target' : 'source'}
            id={pos}
            position={position}
            style={getDotStyle(pos)}
            onMouseEnter={() => setHoveredDot(pos)}
            onMouseLeave={() => setHoveredDot(null)}
          />
        ))}
        <span ref={textRef} className={`relative z-10 mx-1 ${textClasses}`}>
          {label}
        </span>
      </div>
    </Tooltip>
  )
}
